import {Box, IconButton, TextField, Typography} from "@mui/material";
import {FrameIOContent} from "./content/FrameIOContent";
import {AWSContent} from "./content/AWSContent";
import {connect} from "react-redux";
import {selectConfig, selectNetwork} from "../../redux/selectors/firebase";
import {NetworkData} from "../../fstn/network";
import {useEffect, useState} from "react";
import {makeStyles} from "@mui/styles";
import {Cancel, CheckCircle} from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
    check: {
        width: 30
    },
    date: {
        margin: theme.spacing(1),
        width: 90
    },
    dateHeader: {
        margin: theme.spacing(1),
        marginTop: theme.spacing(8),
        width: 90
    },
    blockName: {
        flexGrow: 1,
        fontSize: "x-small",
        margin: theme.spacing(1),
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: 90,
        whiteSpace: "nowrap",
    },
    blockNameHeader: {
        display: "flex",
        flexGrow: 1,
        marginTop: theme.spacing(8),
        margin: theme.spacing(1),
        width: 90
    },
    format: {
        paddingTop: theme.spacing(3),
        marginTop: theme.spacing(8),
        transform: "rotate(315deg)",
        whiteSpace: "nowrap",
        width: 30
    },
    formatLabel: {
        borderBottom: "2px solid lightgrey"
    },
    table: {
        height: "100%",
        marginRight: theme.spacing(10)
    },
    tableContent: {
        maxHeight: "calc(100% - 112px)",
        overflowX: "hidden",
        overflowY: "scroll",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    tableHead: {
    },
}));
const platforms = {
    android: "Android",
    ios: "iPhone",
    osx: "Macintosh",
    win32: "Windows"
};

interface IVideoLevel {
    "1440p"?: string,
    "1080p"?: string,
    "720p"?: string,
    "480p"?: string
}
interface IResourceRegistry {
    jsn_inherit?: string,
    video_level: IVideoLevel
}
interface IConfig {
    resource_registry: {[platform: string]: IResourceRegistry}
}
interface IDashboard {
    aws?: AWSContent
    config?: IConfig
    frameIO?: FrameIOContent
    network: NetworkData
    onClick: (source: string, key: string, format?: string) => void
}
function VideoDashboard({aws, config, frameIO, network, onClick}: IDashboard) {
    const classes = useStyles();
    const [blockNames, setBlockNames] = useState<string[]>([]);
    const [blockNameFilter, setBlockNameFilter] = useState("");
    const [transcodes, setTranscodes] = useState<{[key: string]: string}>({});

    useEffect(() => {
        const blockNames: string[] = [];

        Object.values(network.states || {}).filter(state => state.meta?.blockNames).forEach(state =>
            blockNames.push(...state.meta!.blockNames!.filter(blockName => ! blockNames.includes(blockName)))
        );
        setBlockNames(blockNames
            .filter(blockName => blockName.indexOf(blockNameFilter) >= 0)
            .sort((lhs, rhs) => lhs.localeCompare(rhs)));
    }, [network, blockNameFilter]);
    useEffect(() => {
        if (config) {
            const transcodes: {[name: string]: string} = {};

            Object.entries(platforms).forEach(([key, name]) => {
                const platform = config.resource_registry[key];

                if (platform) {
                    const videoLevels = (platform.jsn_inherit && config.resource_registry[platform.jsn_inherit]) ?
                        {...config.resource_registry[platform.jsn_inherit].video_level, ...platform.video_level} :
                        platform.video_level;

                    Object.entries(videoLevels).forEach(([videoLevel, path]) =>
                        transcodes[`${name} ${videoLevel}`] = path
                    );
                } 
            });
            setTranscodes(transcodes);
        }
    }, [config]);
    if (!frameIO || !aws) return null;
    return (
        <Box className={classes.table}>
            <Box className={classes.tableHead} display="flex">
                <Box className={classes.blockNameHeader}>
                    <Typography><strong>Block Name</strong></Typography>
                    <TextField label="Filter" onChange={({target}) => setBlockNameFilter(target.value)}
                               size="small" sx={{flexGrow: 1, marginLeft: 1}} value={blockNameFilter}/>
                </Box>
                <Box className={classes.dateHeader}><Typography><strong>Modified</strong></Typography></Box>
                <Box className={classes.format}>
                    <Typography><strong className={classes.formatLabel}>Source</strong></Typography>
                </Box>
                {Object.keys(transcodes).map(transcode =>
                    <Box className={classes.format} key={transcode}>
                        <Typography>
                            <strong className={classes.formatLabel}>{transcode}</strong>
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box className={classes.tableContent}>
                {blockNames.map(blockName => {
                    const sourceKey = frameIO.getBlockNameKey(blockName);
                    const source = sourceKey && frameIO.getFile(sourceKey);

                    return (
                        <Box display="flex" key={blockName}>
                            <Box className={classes.blockName} flexGrow={1} title={blockName}>
                                <Typography>{blockName}</Typography>
                            </Box>
                            <Box className={classes.date}>
                                <Typography>
                                    {source ? new Date(source.timestamp).toLocaleDateString() : "Unset"}
                                </Typography>
                            </Box>
                            <Box className={classes.check}>
                                <IconButton color={source ? "primary" : "warning"}
                                            onClick={() => onClick("frameIo", blockName)}>
                                    {source ? <CheckCircle/> : <Cancel/>}
                                </IconButton>
                            </Box>
                            {Object.keys(transcodes).map(transcode => {
                                const fileKey = aws.getBlockNameKey(blockName, transcodes[transcode]);
                                const file = aws.getFile(fileKey);
                                const color = file && source && (file.timestamp > source.timestamp) ? "primary" :
                                    "warning";

                                return (
                                    <Box className={classes.check} key={transcode}>
                                        <IconButton color={color} onClick={() =>
                                            onClick("aws", blockName, transcodes[transcode])}>
                                            {file ? <CheckCircle/> : <Cancel/>} 
                                        </IconButton>
                                    </Box>
                                );
                            })}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
const mapStateToProps = (store: never) => ({
    config: selectConfig(store),
    network: selectNetwork(store)
});
const connected = connect(mapStateToProps, {
})(VideoDashboard);

export {connected as VideoDashboard}; 
